// CampaignReachPanel.jsx — extended reach preview for the campaign composer.
//
// Beyond the basic ReachPanel (count + language/city), this also breaks down
// "will skip" reasons so marketing can see the EFFECTIVE reach for the campaign:
//   - opted out (marketingOptIn === false)
//   - no token (fcmToken falsy)
//   - frequency cap (lastCampaignAt within freqCapHours of scheduled send time)
//   - quiet hours (scheduled send time falls inside quietHours window)
//
// Updates on segment change AND on schedule/quietHours/freqCap change.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardBody } from 'reactstrap';
import { firestore, functions } from '../../../../../helpers/Firebase';
import { fetchAndResolve, validateRuleTree, extractLocationConstraints } from '../../segments/helpers/segmentResolver.js';

const DEBOUNCE_MS = 500;
const SCAN_CAP = 5000;

const pill = (bg, fg = '#fff') => ({
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: 10,
  background: bg,
  color: fg,
  fontSize: '0.75rem',
  fontWeight: 500,
  marginRight: 4,
  marginBottom: 4,
});

// Pure helper — re-implements the quiet-hours check from helpers/isReachable
const inQuietHours = (nowMs, startHour, endHour, timezone = 'Asia/Dubai') => {
  const localHour = parseInt(
    new Date(nowMs).toLocaleString('en-US', { timeZone: timezone, hour: 'numeric', hour12: false }),
    10
  );
  return startHour <= endHour
    ? (localHour >= startHour && localHour < endHour)
    : (localHour >= startHour || localHour < endHour);
};

const CampaignReachPanel = ({ ruleTree, schedule }) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const timerRef = useRef(null);
  const reqIdRef = useRef(0);

  // Stable key — re-run when segment OR schedule fields change
  const depKey = useMemo(() => {
    try {
      return JSON.stringify({
        tree: ruleTree,
        sendAt: schedule?.sendAt,
        startHour: schedule?.quietHours?.startHour,
        endHour: schedule?.quietHours?.endHour,
        freqCap: schedule?.frequencyCapHours,
      });
    } catch { return ''; }
  }, [ruleTree, schedule]);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setError(null);

    // Empty ruleTree (no filters) intentionally means "send to entire
    // reachable base" — see campaignEditor's audience checklist warning.
    // Compute the real reach so the marketer sees the count they're about
    // to broadcast to, instead of a misleading "add a rule" placeholder.
    const effectiveTree = ruleTree || { match: 'all', rules: [] };
    const errors = validateRuleTree(effectiveTree);
    if (errors.length) { setValidationErrors(errors); setResult(null); return; }
    setValidationErrors([]);

    timerRef.current = setTimeout(async () => {
      const myReq = ++reqIdRef.current;
      setLoading(true);
      try {
        // Prefer the server-side callable — routes to Data Connect for real
        // reach across the full base (no SCAN_CAP), computes the skip breakdown
        // and location constraints server-side. Falls back to the client scan
        // if the callable isn't available (pre-cutover deploy).
        try {
          const call = functions().httpsCallable('queryMarketingSegment');
          const resp = await call({ ruleTree: effectiveTree, schedule });
          if (myReq !== reqIdRef.current) return;
          if (resp?.data) {
            setResult(resp.data);
            return;
          }
        } catch (callableErr) {
          console.warn('[CampaignReachPanel] queryMarketingSegment unavailable, falling back to client scan', callableErr?.message);
        }

        // Legacy client-side path — kept for the transition window.
        // First pass: get raw candidates with reachability OFF so we can break down skip reasons
        const raw = await fetchAndResolve(
          firestore().collection('user_profiles_marketing'),
          effectiveTree,
          { limit: SCAN_CAP, enforceReachability: false }
        );
        if (myReq !== reqIdRef.current) return;

        const candidates = raw.matches;
        const sendAt = schedule?.sendAt || Date.now();
        const freqCapMs = (schedule?.frequencyCapHours || 0) * 3600 * 1000;
        const startHour = schedule?.quietHours?.startHour ?? 22;
        const endHour = schedule?.quietHours?.endHour ?? 8;
        const inQuiet = inQuietHours(sendAt, startHour, endHour);

        let optedOut = 0, noToken = 0, freqCap = 0, quietSkip = 0;
        const reachable = [];
        for (const p of candidates) {
          if (p.marketingOptIn === false) { optedOut++; continue; }
          if (!p.fcmToken) { noToken++; continue; }
          if (freqCapMs > 0 && p.lastCampaignAt && (sendAt - p.lastCampaignAt) < freqCapMs) { freqCap++; continue; }
          if (inQuiet) { quietSkip++; continue; }
          reachable.push(p);
        }

        // Location breakdowns respect the segment's location constraints. If the rule
        // filters on emirate/city, the breakdown only counts the values in the rule
        // (so `allEmirates contains Dubai` shows "Dubai: N", not every other emirate
        // those users happen to have saved). No constraint → aggregate over all
        // saved places. Falls back to primary field on legacy profiles.
        const constraints = extractLocationConstraints(effectiveTree);
        const hasCityConstraint = constraints.cities.size > 0;
        const hasEmirateConstraint = constraints.emirates.size > 0;

        const byLanguage = {};
        const byCity = {};
        const byEmirate = {};
        for (const p of reachable) {
          byLanguage[p.language || 'unknown'] = (byLanguage[p.language || 'unknown'] || 0) + 1;
          const cities = Array.isArray(p.allCities) && p.allCities.length > 0
            ? p.allCities
            : (p.city ? [p.city] : []);
          const filteredCities = hasCityConstraint
            ? cities.filter(c => constraints.cities.has(c))
            : cities;
          for (const c of (filteredCities.length ? filteredCities : ['unknown'])) {
            byCity[c] = (byCity[c] || 0) + 1;
          }
          const emirates = Array.isArray(p.allEmirates) && p.allEmirates.length > 0
            ? p.allEmirates
            : (p.emirate ? [p.emirate] : []);
          const filteredEmirates = hasEmirateConstraint
            ? emirates.filter(e => constraints.emirates.has(e))
            : emirates;
          for (const e of (filteredEmirates.length ? filteredEmirates : ['unknown'])) {
            byEmirate[e] = (byEmirate[e] || 0) + 1;
          }
        }
        const topCities = Object.entries(byCity)
          .sort((a, b) => b[1] - a[1]).slice(0, 5)
          .map(([city, count]) => ({ city, count }));
        const topEmirates = Object.entries(byEmirate)
          .sort((a, b) => b[1] - a[1]).slice(0, 7)
          .map(([emirate, count]) => ({ emirate, count }));

        setResult({
          candidateCount: raw.candidateCount,
          matchedCount: candidates.length,
          reachableCount: reachable.length,
          hitLimit: raw.hitLimit,
          strategy: raw.strategy,
          skips: { optedOut, noToken, freqCap, quietSkip },
          byLanguage,
          topCities,
          topEmirates,
          cityBreakdownConstrained: hasCityConstraint,
          emirateBreakdownConstrained: hasEmirateConstraint,
        });
      } catch (e) {
        if (myReq === reqIdRef.current) setError(e.message);
      } finally {
        if (myReq === reqIdRef.current) setLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [depKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Card className="mb-3">
      <CardBody>
        <h6 className="mb-3">Audience reach</h6>

        {validationErrors.length > 0 && (
          <div className="text-warning"><small>
            <strong>Fix segment to see reach:</strong>
            <ul className="mb-0 mt-1 pl-3">
              {validationErrors.slice(0, 3).map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          </small></div>
        )}

        {!validationErrors.length && error && (
          <div className="text-danger"><small>Error: {error}</small></div>
        )}

        {!validationErrors.length && !error && !result && !loading && (
          <div className="text-muted"><small>Add a rule, or leave blank to target every reachable user.</small></div>
        )}

        {loading && <div className="text-muted"><small>Counting…</small></div>}

        {result && !loading && (
          <>
            {/* Explicit callout when the audience has no filters — makes the
                "empty = broadcast to everyone" behavior visible so a marketer
                can't accidentally schedule a base-wide send without knowing. */}
            {(!ruleTree || !Array.isArray(ruleTree.rules) || ruleTree.rules.length === 0) && (
              <div className="mb-2">
                <span style={pill('#f0ad4e')}>No filters — targeting entire reachable base</span>
              </div>
            )}

            <div className="d-flex align-items-baseline mb-1">
              <span style={{ fontSize: '2rem', fontWeight: 600, marginRight: 8, color: '#3498db' }}>
                {result.reachableCount.toLocaleString()}
              </span>
              <span className="text-muted"><small>will receive</small></span>
            </div>

            <div className="text-muted small mb-3">
              Matched {result.matchedCount.toLocaleString()} · Scanned {result.candidateCount.toLocaleString()}
              {result.hitLimit && (
                <div className="mt-1">
                  <span style={pill('#f0ad4e')}>Capped at {SCAN_CAP.toLocaleString()} — true count may be higher</span>
                </div>
              )}
            </div>

            {/* Skip breakdown */}
            {Object.values(result.skips).some(n => n > 0) && (
              <div className="mb-3">
                <div className="text-muted small mb-1">Will skip</div>
                <div>
                  {result.skips.optedOut > 0 && <span style={pill('#e9ecef', '#495057')}>opted out: {result.skips.optedOut}</span>}
                  {result.skips.noToken > 0 && <span style={pill('#e9ecef', '#495057')}>no token: {result.skips.noToken}</span>}
                  {result.skips.freqCap > 0 && <span style={pill('#e9ecef', '#495057')}>freq cap: {result.skips.freqCap}</span>}
                  {result.skips.quietSkip > 0 && <span style={pill('#e9ecef', '#495057')}>quiet hours: {result.skips.quietSkip}</span>}
                </div>
              </div>
            )}

            {Object.keys(result.byLanguage).length > 0 && (
              <div className="mb-2">
                <div className="text-muted small mb-1">By language</div>
                <div>
                  {Object.entries(result.byLanguage).map(([lang, n]) => (
                    <span style={pill('#e9ecef', '#495057')} key={lang}>{lang}: {n.toLocaleString()}</span>
                  ))}
                </div>
              </div>
            )}

            {result.topEmirates && result.topEmirates.length > 0 && (
              <div className="mb-2">
                <div className="text-muted small mb-1">
                  Top emirates <span className="text-muted" style={{ fontWeight: 400 }}>
                    ({result.emirateBreakdownConstrained ? 'matches your rule' : 'any saved place'})
                  </span>
                </div>
                <div>
                  {result.topEmirates.map(({ emirate, count }) => (
                    <span style={pill('#e9ecef', '#495057')} key={emirate}>{emirate}: {count.toLocaleString()}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Top cities breakdown hidden — city fields no longer exposed in the picker. */}
          </>
        )}
      </CardBody>
    </Card>
  );
};

export default CampaignReachPanel;
